import { HttpClient } from '@angular/common/http'
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core'
import { FormControl, FormGroup } from '@angular/forms'
import { Router } from '@angular/router'
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs'
import { map, take } from 'rxjs/operators'

import { Enums } from '@cyborg-monorepo/hunter/common'

import { CyborgFilterComponent } from '../../../shared/components/cyborg-filter/cyborg-filter.component'
import { HeaderComponent } from '../../../shared/components/header/header.component'
import { UserService } from '../../../shared/services/user.service'
import {
  DEFAULT_SEARCH_STATE,
  SearchStateExtended,
  SearchStateService,
  TagType,
} from '../../services/search-state.service'
import { SearchUrlService } from '../../services/search-url.service'

@Component({
  standalone: false,
  templateUrl: './filter.component.html',
  styleUrls: ['filter.component.scss'],
})
export class FilterComponent implements OnInit, OnDestroy, AfterViewInit {
  private http = inject(HttpClient)
  searchStateService = inject(SearchStateService)
  private changeDetectorRef = inject(ChangeDetectorRef)
  private searchUrlService = inject(SearchUrlService)
  private router = inject(Router)
  private userService = inject(UserService)

  @ViewChild(HeaderComponent) public header: HeaderComponent
  @ViewChild(CyborgFilterComponent) public cyborgFilter: CyborgFilterComponent
  @ViewChild('mainContent') public mainContent: ElementRef<HTMLDivElement>

  public cyborgIndexEnum = Enums.CyborgIndexEnum
  public filteredTags: TagType[] = []
  public filteredMitre: TagType[] = []
  public filteredKillChains: TagType[] = []
  public filteredTools: TagType[] = []
  public filteredPlatformTypes: TagType[] = []
  public filteredDataSources: TagType[] = []
  public filteredDependencyTypes: TagType[] = []
  public filteredGoals: TagType[] = []
  public term = ['*']
  public touched: boolean
  public community: boolean

  public isShowingFilters = false
  public isTrial$: Observable<boolean>

  public contextualTagsSubject: Subject<string> = new Subject<string>()
  public mitreTagsSubject: Subject<string> = new Subject<string>()
  public huntPackageCount = 0
  public profileCount = 0
  public collectionCount = 0
  public searchState$: BehaviorSubject<SearchStateExtended>
  public library: Enums.CyborgIndexEnum[]
  public searchForm: FormGroup

  private stateSubscription: Subscription
  private resultsSubscription: Subscription

  public ngOnInit(): void {
    this.searchForm = new FormGroup({
      search: new FormControl(''),
    })
    this.searchState$ = this.searchStateService.state
    this.isTrial$ = this.userService.profile$.pipe(
      map((profile) => profile?.company.status === Enums.CompanyStatus.TRIAL),
    )

    this.stateSubscription = this.searchStateService.state.subscribe(
      (state: SearchStateExtended) => {
        this.term = state.term
        this.library = state.library
        this.touched = state.touched
        this.community = state.community
      },
    )

    this.resultsSubscription = this.searchStateService.results.subscribe(
      (results) => {
        this.filteredMitre = results.facets.mitreTags
        this.filteredKillChains = results.facets.killChains

        this.filteredTags = results.facets.tags.sort((a, b) => {
          const typeA = a.type.toLowerCase()
          const typeB = b.type.toLowerCase()
          return typeA.localeCompare(typeB)
        })

        this.filteredTools = results.facets.tools

        const behavior = results.facets.goals[0]?.tags.find(
          (tag) => tag.key === 'Behavior',
        )
        const hunt = results.facets.goals[0]?.tags.find(
          (tag) => tag.key === 'Hunt',
        )
        const visual = results.facets.goals[0]?.tags.find(
          (tag) => tag.key === 'Visual',
        )
        const targeted = results.facets.goals[0]?.tags.find(
          (tag) => tag.key === 'Targeted',
        )

        const newTags: TagType[] = [
          { type: 'Hunt', tags: [] },
          { type: 'Detection', tags: [] },
        ]

        if (behavior) newTags[1].tags.push(behavior)
        if (targeted) newTags[1].tags.push(targeted)
        if (hunt) newTags[0].tags.push(hunt)
        if (visual) newTags[0].tags.push(visual)

        this.filteredGoals = newTags

        this.filteredPlatformTypes = results.facets.platformTypes
        this.filteredDataSources = results.facets.dataSources
        this.filteredDependencyTypes = results.facets.dependencies

        const counts = results.data.count
        this.huntPackageCount = counts.cyborg_hunt_packages ?? 0
        this.profileCount = counts.cyborg_threat_profile ?? 0
        this.collectionCount = counts.cyborg_collections ?? 0

        if (this.router.url.startsWith('/research/search')) {
          this.mainContent?.nativeElement?.scrollTo({ top: 0 })
          window.scrollTo({ top: 0 })
        }
      },
    )

    this.showFilterPane() // Also sets isShowingFilters = false
  }

  public ngAfterViewInit(): void {
    this.changeDetectorRef.detectChanges()
  }

  public ngOnDestroy(): void {
    this.stateSubscription?.unsubscribe()
    this.resultsSubscription?.unsubscribe()
  }

  public onLibraryFilter(target: Enums.CyborgIndexEnum): void {
    if (!this.touched) {
      this.searchStateService.setLibrary([target], true)
    } else {
      if (!this.library.includes(target)) {
        this.searchStateService.addLibrary(target, true)
      } else {
        if (this.library.length > 1) {
          this.searchStateService.removeLibrary(target, true)
        } else {
          this.searchStateService.setLibrary(
            DEFAULT_SEARCH_STATE.library,
            false,
          )
        }
      }
    }
  }

  public onContextualSearch(value: string): void {
    this.searchStateService.results.pipe(take(1)).subscribe((resultState) => {
      this.filteredTags = resultState.facets.tags.filter((section) =>
        section.tags.some((tag) =>
          tag.key.toLowerCase().includes(value.trim().toLowerCase()),
        ),
      )
      this.changeDetectorRef.detectChanges()
    })
    this.contextualTagsSubject.next(value)
  }

  public onMitreSearch(value: string): void {
    this.searchStateService.results.pipe(take(1)).subscribe((resultState) => {
      this.filteredMitre = resultState.facets.mitreTags.filter((section) =>
        section.tags.some((tag) =>
          tag.key.toLowerCase().includes(value.trim().toLowerCase()),
        ),
      )
      this.changeDetectorRef.detectChanges()
    })
    this.mitreTagsSubject.next(value)
  }

  public toggleFilterPane(): void {
    const filterSide = document.querySelector(`#dashboard main`)
    filterSide?.classList.toggle('is-showing-filters')
    this.isShowingFilters =
      filterSide?.classList.contains('is-showing-filters') ?? false
  }

  public showFilterPane(): void {
    const filterSide = document.querySelector(`#dashboard main`)
    filterSide?.classList.remove('is-showing-filters')
    this.isShowingFilters = false
  }

  public hideFilterPane(): void {
    const filterSide = document.querySelector(`#dashboard main`)
    filterSide?.classList.add('is-showing-filters')
    this.isShowingFilters = true
  }

  public onContentClose(): void {
    const charts = document.getElementById('charts') as HTMLElement
    charts.classList.toggle('is-closed')
  }

  public onCompatible(target: EventTarget): void {
    if ((target as HTMLInputElement).checked) {
      this.searchStateService.setCompatible()
    } else {
      this.searchStateService.resetCompatible()
    }
  }

  public onCommunity(): void {
    if (this.community) {
      this.searchStateService.resetCommunity()
    } else {
      this.searchStateService.setCommunity()
    }
  }

  public onLastUpdated(event: EventTarget): void {
    const target = event as HTMLInputElement
    const lastUpdate = Number(target.getAttribute('value'))

    this.searchStateService.setLastUpdated(lastUpdate)
  }

  public onSearch(): void {
    const term: string = this.searchForm.value.search
    if (term && !this.searchStateService.state.value.term.includes(term)) {
      this.searchStateService.setTerm(term)
      this.searchUrlService.gotoUrl(this.searchStateService.state.value)
      this.searchForm.setValue({ search: '' })
    }
  }
}
